/**
 * ICD v1.0 shared enumeration schemas (docs/elevate/ICD-v1.0.md section 8).
 * Canonical enumeration codes cross the boundary; display strings never do.
 * Each vocabulary is defined once here and reused by every view, payload and
 * event schema. Declarations only: no runtime logic, no consumers.
 */
import { z } from 'zod';

/** ICD section 8.1: dimension "how many" (acquisition), worst to best. */
export const HowManyV1 = z.enum([
  'few_hardly_any',
  'some',
  'many',
  'most',
  'all_almost_all',
]);

/** ICD section 8.2: dimension "how often" (robustness), worst to best. */
export const HowOftenV1 = z.enum([
  'rarely',
  'occasionally',
  'regularly',
  'routinely',
  'always_almost_always',
]);

/** ICD section 8.3: dimension "TEM outcome" (effectiveness), worst to best. */
export const TemOutcomeV1 = z.enum([
  'safety_compromised',
  'safety_momentarily_reduced',
  'safety_maintained',
  'safety_improved',
  'safety_optimized',
]);

/** ICD section 8.4: which dimension set the grade (stored, never recomputed). */
export const LimitingFactorV1 = z.enum(['how_many', 'how_often', 'tem_outcome']);

/** ICD section 8.4: override discrepancy category (DEC-28), mandatory on every override. */
export const OverrideCategoryV1 = z.enum([
  'evidence_not_captured_by_dimensions',
  'dimension_recorded_in_error',
  'tem_outcome_not_attributable',
  'other',
]);

/** ICD section 8.5: observable behaviour origin discriminator. */
export const BehaviourOriginV1 = z.enum([
  'reference',
  'operator_derived',
  'operator_competency',
]);

/** ICD section 8.6: the contract grade value, integer 1 to 5. */
export const GradeV1 = z.number().int().min(1).max(5);

/** ICD section 8.6: grade labels (presentation only; the number is the contract). */
export const GradeLabelV1 = z.enum([
  'Unsafe',
  'Below Standard',
  'Adequate',
  'Above Standard',
  'Exemplary',
]);

/** ICD section 8.6: the grade-label derivation rule (labels derive from numeric value). */
export const GRADE_LABEL_BY_VALUE = {
  1: 'Unsafe',
  2: 'Below Standard',
  3: 'Adequate',
  4: 'Above Standard',
  5: 'Exemplary',
} as const;

/** ICD section 8.8: development level (fixed for every operator, not adaptable). */
export const DevelopmentLevelV1 = z.enum([
  'beginner',
  'building_competencies',
  'proficient',
]);

/** ICD section 8.8: source of the current development level. */
export const DevelopmentLevelSourceV1 = z.enum([
  'default_mapping',
  'instructor_override',
  'operator_setting',
]);

/** ICD section 8.9: instructor action type (INT-03). */
export const InstructorActionTypeV1 = z.enum(['level_assignment', 'attempt_cycle_reset']);

/** ICD section 8.10: pathway statuses (PRS Appendix A). */
export const PathwayStatusV1 = z.enum([
  'recommended',
  'mandatory_recommendation',
  'self_selected',
  'in_progress',
  'remedial_content_assigned',
  'learning_completed',
  'awaiting_practical_reassessment',
  'improvement_demonstrated',
  'further_development_required',
  'declined',
  'withdrawn',
  'superseded',
]);

/** ICD section 8.11 (informative): the Framework Listing Order, OPR always last. */
export const FRAMEWORK_LISTING_ORDER_V1 = [
  'KNO',
  'FPM',
  'PRO',
  'FPA',
  'COM',
  'WLM',
  'PSD',
  'LTW',
  'SAW',
  'OPR',
] as const;

/** ICD section 14 OP-4: evidence item type (single evidenced type in v1.0). */
export const EvidenceItemTypeV1 = z.enum(['quote']);

/** ICD section 3.2 common contract: acknowledgement result. */
export const WriteResultV1 = z.enum(['applied', 'replayed']);

/** ICD section 5: error catalog codes (R = retryable is ERR_TRANSIENT only). */
export const ErrorCodeV1 = z.enum([
  'ERR_UNRESOLVABLE_CODE',
  'ERR_UNKNOWN_FRAMEWORK_VERSION',
  'ERR_EXPIRED_FRAMEWORK_VERSION',
  'ERR_UNPUBLISHED_FRAMEWORK_VERSION',
  'ERR_ENTITLEMENT_REJECTED',
  'ERR_IDEMPOTENCY_CONFLICT',
  'ERR_SCHEMA_INVALID',
  'ERR_UNSUPPORTED_CONTRACT_VERSION',
  'ERR_UNKNOWN_REFERENCE',
  'ERR_INVALID_STATUS_TRANSITION',
  'ERR_TRANSIENT',
]);

/** ICD section 3.1.2/14 OP-4: one trainee-visible evidence item. */
export const EvidenceItemV1 = z.object({
  type: EvidenceItemTypeV1,
  timestamp: z.string().nullable(),
  text: z.string(),
});
